/**
 * OVERVOLT — Line-of-sight (LOS) wall geometry for puzzle mode.
 *
 * A puzzle level can mark tiles as impenetrable **LOS walls** — terrain that
 * blocks the straight line of fire from a tower to an enemy. This module is
 * the **pure, deterministic geometry primitive** the simulation will call to
 * filter shots: one **grid-DDA ray test** that walks the segment from `a` to
 * `b` in tile units and reports the first time the trace enters a wall tile.
 *
 * Sprint 30 (puzzle-impl-s30) — the module is shipped **standalone**: it is
 * exported for systems / tools / tests but is **not yet integrated** into
 * `targetsInRange` / `fireSniper`. That follow-up integration is a tiny
 * commit after `abilities-s20b` (which is concurrently editing `systems.ts`)
 * lands. See docs/PUZZLE_DESIGN.md §2 for the integration recipe.
 *
 * Key properties (every one is asserted by `tests/los.spec.ts`):
 *
 *  - **Pure**: no module-level state, no RNG, no hidden caches.
 *  - **Allocation-free**: every step walks integers in registers; no Sets,
 *    Maps, or arrays are constructed per call.
 *  - **Endpoint-safe**: the tile holding the shooter and the tile holding the
 *    target are NEVER tested. A tower can fire from a tile immediately
 *    adjacent to a wall; a wall tile is never path-walkable so an enemy
 *    cannot sit on one. Only INTERMEDIATE tiles gate the shot.
 *  - **Mortar exempt by caller**: the Mortar's parabolic arc is the indirect-
 *    fire payoff for the Sprint 14 min-range mechanic and is explicitly
 *    LOS-exempt. This module never makes that decision — the caller does, by
 *    not calling `losBlocked` for Mortar shells (see PUZZLE_DESIGN.md §2.4).
 *
 * Algorithm: Amanatides–Woo grid traversal (1987) — the classic DDA for
 * marching a parametric ray through a uniform grid. The inner loop touches at
 * most one tile per integer step, with a worst-case bound of
 * `|Δcol| + |Δrow| + 1` tiles for any segment that crosses the board — i.e.
 * ≤ 32 tiles on the 20×12 OVERVOLT grid. Three integer comparisons + one
 * float compare per step; cheap enough that we can afford the test for every
 * `(tower, enemy)` pair every tick (≤ 1792 comparisons/tick in §2.5's
 * worst-case envelope).
 */
import type { Point } from './map';

/**
 * Pack `(col, row)` into the single integer key used by
 * `state.losWalls: ReadonlySet<number>`. Mirrors `tileIndex` in
 * `pathfinding.ts`, but kept local so this module has no internal-package
 * dependencies (a future shared puzzle / share-string format can serialise
 * walls as raw `Set<number>` without dragging in the pathfinding module).
 */
export function losTileKey(col: number, row: number, cols: number): number {
  return row * cols + col;
}

/**
 * Build the packed `ReadonlySet<number>` for a list of wall cells. Pure helper
 * exposed for `puzzle.ts` / tests; the per-tick fire path never calls it.
 */
export function packLosWalls(
  walls: ReadonlyArray<{ col: number; row: number }>,
  cols: number,
): ReadonlySet<number> {
  const out = new Set<number>();
  for (const c of walls) out.add(c.row * cols + c.col);
  return out;
}

/**
 * Grid-DDA ray test — true iff the open segment from `a` to `b` (pixel
 * coordinates in playfield-local space) crosses any wall tile in `walls`.
 *
 * **Endpoint tiles are excluded**, in keeping with the integration model: the
 * shooter's tile and the target's tile are by construction never walls (a
 * wall is unbuildable and unwalkable), so excluding them costs the caller
 * nothing and saves one tile-lookup per call.
 *
 * Returns `false` immediately when `walls` is empty / null-ish (`size === 0`),
 * when `a` and `b` lie in the same tile (no intermediate tiles), or when the
 * caller is outside the grid bounds (off-board callers can never hit a wall
 * tile because every wall is in-bounds by definition).
 *
 * @param walls  Packed tile-index set; usually `state.losWalls`.
 * @param a      Source pixel position (tower centre).
 * @param b      Target pixel position (enemy position).
 * @param tileSize  Side length of a grid tile in pixels (`CONFIG.grid.tileSize`).
 * @param cols   Grid width in tiles (`CONFIG.grid.cols`).
 * @param rows   Grid height in tiles (`CONFIG.grid.rows`).
 * @returns `true` when a wall blocks the line of fire; `false` otherwise.
 */
export function losBlocked(
  walls: ReadonlySet<number> | null | undefined,
  a: Point,
  b: Point,
  tileSize: number,
  cols: number,
  rows: number,
): boolean {
  if (!walls || walls.size === 0) return false;
  // Convert pixel positions to fractional tile coordinates. The DDA walks the
  // ray in this fractional-tile space — clean integer cell transitions, no
  // pixel-level aliasing.
  const ax = a.x / tileSize;
  const ay = a.y / tileSize;
  const bx = b.x / tileSize;
  const by = b.y / tileSize;
  const startCol = Math.floor(ax);
  const startRow = Math.floor(ay);
  const endCol = Math.floor(bx);
  const endRow = Math.floor(by);
  // Trivial case — both endpoints in the same tile, no intermediates to test.
  if (startCol === endCol && startRow === endRow) return false;
  const dx = bx - ax;
  const dy = by - ay;

  // Amanatides–Woo step directions in tile units. `±1` per axis; `0` when the
  // ray is axis-aligned in that direction (the corresponding `tMax` is set to
  // `Infinity` so the algorithm never steps that axis).
  const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;
  // `tMax{X,Y}` — parametric distance to the NEXT tile boundary on each axis.
  // `tDelta{X,Y}` — parametric distance per tile crossed on each axis.
  // Parametric `t` runs 0 at `a`, 1 at `b`; the ray reaches `b` when `t = 1`.
  const tMaxX = dx === 0 ? Infinity : ((stepX > 0 ? startCol + 1 - ax : ax - startCol) / Math.abs(dx));
  const tMaxY = dy === 0 ? Infinity : ((stepY > 0 ? startRow + 1 - ay : ay - startRow) / Math.abs(dy));
  const tDeltaX = dx === 0 ? Infinity : 1 / Math.abs(dx);
  const tDeltaY = dy === 0 ? Infinity : 1 / Math.abs(dy);

  let col = startCol;
  let row = startRow;
  let tx = tMaxX;
  let ty = tMaxY;
  // Worst-case step budget: every grid tile crossable along the segment, plus
  // a safety margin. `|Δcol| + |Δrow| + 2` is the tight bound for an
  // axis-aligned or pure-diagonal traversal; `+ 2` for the corner-clip cases.
  const maxSteps = Math.abs(endCol - startCol) + Math.abs(endRow - startRow) + 2;
  for (let i = 0; i < maxSteps; i += 1) {
    // Advance to the next tile boundary — pick the closer one (smaller `t`).
    // On an exact tie (corner crossing) we advance both axes simultaneously,
    // which means the trace skips the diagonally-touched tile rather than
    // entering it — geometrically correct for a wall hugging a corner.
    if (tx < ty) {
      col += stepX;
      tx += tDeltaX;
    } else if (ty < tx) {
      row += stepY;
      ty += tDeltaY;
    } else {
      // Corner crossing — step both axes at once.
      col += stepX;
      row += stepY;
      tx += tDeltaX;
      ty += tDeltaY;
    }
    // Reached (or stepped past) the target tile — every remaining tile is
    // the target tile itself, which is endpoint-excluded.
    if (col === endCol && row === endRow) return false;
    // Off-board step — every wall tile is in-bounds, so an off-board step
    // means no wall can be hit on the remainder of the ray; the loop bails
    // safely. (Robustness: the cast/blast logic should never call us with
    // out-of-bounds pixels, but the geometry stays sane if it does.)
    if (col < 0 || row < 0 || col >= cols || row >= rows) return false;
    // Intermediate tile — test it. Hit on the first wall.
    if (walls.has(row * cols + col)) return true;
  }
  // The bound was hit without reaching `(endCol, endRow)`. This should be
  // unreachable for well-formed inputs (the DDA terminates in at most
  // `|Δcol| + |Δrow|` axis-steps); on a numerical edge case we conservatively
  // report "not blocked" rather than spuriously cancelling a shot.
  return false;
}

/**
 * Test helper / Sniper-lance helper — return the parametric distance `t ∈ [0, 1]`
 * at which the segment from `a` to `b` first hits a wall tile (the segment
 * length is `|b − a|`, so the pixel distance is `t × |b − a|`). Returns `null`
 * when the segment is unobstructed. Endpoint tiles are excluded — see
 * {@link losBlocked}.
 *
 * Used by the (future, Sprint 31 follow-up) Sniper lance wall-clip
 * early-termination: cache `wallStopAlong` once per shot, then bail any beam
 * scoring past it. Shipped now so the contract is fixed and tests can pin
 * the geometry across the abilities-s20b handoff.
 */
export function losFirstHitT(
  walls: ReadonlySet<number> | null | undefined,
  a: Point,
  b: Point,
  tileSize: number,
  cols: number,
  rows: number,
): number | null {
  if (!walls || walls.size === 0) return null;
  const ax = a.x / tileSize;
  const ay = a.y / tileSize;
  const bx = b.x / tileSize;
  const by = b.y / tileSize;
  const startCol = Math.floor(ax);
  const startRow = Math.floor(ay);
  const endCol = Math.floor(bx);
  const endRow = Math.floor(by);
  if (startCol === endCol && startRow === endRow) return null;
  const dx = bx - ax;
  const dy = by - ay;
  const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;
  const tMaxX = dx === 0 ? Infinity : ((stepX > 0 ? startCol + 1 - ax : ax - startCol) / Math.abs(dx));
  const tMaxY = dy === 0 ? Infinity : ((stepY > 0 ? startRow + 1 - ay : ay - startRow) / Math.abs(dy));
  const tDeltaX = dx === 0 ? Infinity : 1 / Math.abs(dx);
  const tDeltaY = dy === 0 ? Infinity : 1 / Math.abs(dy);

  let col = startCol;
  let row = startRow;
  let tx = tMaxX;
  let ty = tMaxY;
  const maxSteps = Math.abs(endCol - startCol) + Math.abs(endRow - startRow) + 2;
  for (let i = 0; i < maxSteps; i += 1) {
    let tEnter: number;
    if (tx < ty) {
      tEnter = tx;
      col += stepX;
      tx += tDeltaX;
    } else if (ty < tx) {
      tEnter = ty;
      row += stepY;
      ty += tDeltaY;
    } else {
      tEnter = tx;
      col += stepX;
      row += stepY;
      tx += tDeltaX;
      ty += tDeltaY;
    }
    if (col === endCol && row === endRow) return null;
    if (col < 0 || row < 0 || col >= cols || row >= rows) return null;
    if (walls.has(row * cols + col)) return tEnter;
  }
  return null;
}
